import { useState } from "react";
import { useCart } from "../context/CartContext";
import emailjs from "@emailjs/browser";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import "./Checkout.scss";

export default function Checkout() {
  const { cart, clearCart } = useCart();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });

  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [orderTotal, setOrderTotal] = useState(0);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const generateOrderId = () => {
    const now = new Date();
    return `CODACO-${now.getFullYear()}${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}${String(now.getDate()).padStart(2, "0")}-${String(
      now.getHours()
    ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(
      now.getSeconds()
    ).padStart(2, "0")}`;
  };

  /* === STACKED  */
  const generateItemsHtml = () => {
    return cart
      .map(
        (item, idx) => `
      <div class="item-block" style="
        padding:12px 14px;
        margin:12px 0;
        border-radius:8px;
        border:1px solid #dce3f0;
        background:${idx % 2 === 0 ? "#f9fbff" : "#eef3ff"};
        font-size:14px;
      ">
        <div style="font-weight:600; color:#003f7d; margin-bottom:6px;">
          ${item.name} (${item.size || "bez velikosti"})
        </div>

        <table width="100%" style="font-size:13px; color:#222;">
          <tr>
            <td align="left" style="padding:4px 0;">${item.quantity} ks</td>
            <td align="right" style="padding:4px 0;">
              ${(item.price * item.quantity).toLocaleString("cs-CZ")} Kč
            </td>
          </tr>
        </table>
      </div>
    `
      )
      .join("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.consent) {
      alert("Musíte souhlasit se zpracováním osobních údajů.");
      return;
    }

    if (cart.length === 0) {
      alert("Košík je prázdný.");
      return;
    }

    const newOrderId = generateOrderId();
    setOrderId(newOrderId);
    setOrderTotal(total);

    const itemsHtml = generateItemsHtml();

    try {
      await addDoc(collection(db, "orders"), {
        orderId: newOrderId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone || "",
        message: form.message || "",
        total,
        items: cart.map((item) => ({
          name: item.name,
          size: item.size || "bez velikosti",
          quantity: item.quantity,
          price: item.price,
        })),
        createdAt: serverTimestamp(),
      });

      const serviceID = "service_sizjugb";
      const templateID = "template_lmqft1f";
      const publicKey = "pXPR8lP3CgV71xnx5";

      const templateParams = {
        orderId: newOrderId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone || "neuvedeno",
        message: form.message || "—",
        total: total.toLocaleString("cs-CZ"),
        year: new Date().getFullYear(),
        itemsHtml,
      };

      await emailjs.send(serviceID, templateID, templateParams, publicKey);

      setOrderSuccess(true);
      clearCart();
    } catch (error) {
      console.error("❌ Email error:", error);
      alert("Došlo k chybě při odesílání objednávky.");
    }
  };

  if (orderSuccess) {
    return (
      <section className="checkout checkout--success">
        <div className="checkout__success-box">
          <h1 className="checkout__success-title">Objednávka přijata!</h1>

          <p className="checkout__success-text">
            Děkujeme, <strong>{form.firstName}</strong>. Objednávka{" "}
            <strong>{orderId}</strong> byla úspěšně přijata.
          </p>

          <div className="checkout__summary">
            <p>
              <strong>Celkem:</strong> {orderTotal.toLocaleString("cs-CZ")} Kč
            </p>
          </div>

          <p className="checkout__back">
            Potvrzení dorazí e-mailem. Zkontrolujte i{" "}
            <span className="checkout__highlight">Spam</span>.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="checkout">
      <div className="checkout__scroller">
        <h1 className="checkout__title">Dokončení objednávky</h1>

        {cart.length === 0 ? (
          <p className="checkout__empty">Košík je prázdný</p>
        ) : (
          <>
            <ul className="checkout__list">
              {cart.map((item, idx) => (
                <li key={idx} className="checkout__item">
                  <div className="checkout__item-info">
                    {item.images?.[0] && (
                      <img
                        src={item.images[0]}
                        alt={item.name}
                        className="checkout__item-thumb"
                        loading="lazy"
                      />
                    )}

                    <div className="checkout__item-text">
                      <span className="checkout__item-name">
                        {item.name} × {item.quantity}
                      </span>

                      {item.size && (
                        <span className="checkout__item-size">
                          Velikost: {item.size}
                        </span>
                      )}
                    </div>
                  </div>

                  <span className="checkout__price">
                    {(item.price * item.quantity).toLocaleString("cs-CZ")} Kč
                  </span>
                </li>
              ))}
            </ul>

            <p className="checkout__total">
              Celkem k úhradě: {total.toLocaleString("cs-CZ")} Kč
            </p>

            <form className="checkout__form" onSubmit={handleSubmit}>
              <label className="checkout__label">
                <span className="checkout__required">*</span> Jméno
              </label>
              <input
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                required
                className="checkout__input"
              />

              <label className="checkout__label">
                <span className="checkout__required">*</span> Příjmení
              </label>
              <input
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                required
                className="checkout__input"
              />

              <label className="checkout__label">
                <span className="checkout__required">*</span> Email
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className="checkout__input"
              />

              <label className="checkout__label">Telefon</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="checkout__input"
              />

              <label className="checkout__label">Zpráva</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                className="checkout__textarea"
              />

              <p className="checkout__note">
                <span className="checkout__required">*</span> označuje povinný
                údaj
              </p>

              <label className="checkout__consent">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={handleChange}
                  required
                />
                Souhlasím se{" "}
                <a
                  href="https://www.codaco.cz/bezpecnost-a-ochrana-osobnich-udaju"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  zpracováním osobních údajů
                </a>
              </label>

              <button
                type="submit"
                className="btn btn--primary checkout__button"
              >
                Odeslat objednávku
              </button>
            </form>
          </>
        )}
      </div>
    </section>
  );
}
